import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useParams, Link } from 'react-router-dom';
import { getComicDetail, type ComicDetail } from '@/mocks/comicData';

function ComicHero({ comic }: { comic: ComicDetail }) {
  return (
    <section className="relative w-full h-[300px] md:h-[420px] overflow-hidden">
      <img
        src={comic.coverUrl}
        alt={`韩漫在线观看 - ${comic.title}`}
        title={`免费韩国漫画下拉式 - ${comic.title}`}
        className="absolute inset-0 w-full h-full object-cover object-top"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/60"></div>
      <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
        <div className="max-w-6xl mx-auto w-full px-4 md:px-6">
          <h1 className="text-2xl md:text-4xl font-heading font-bold text-background-50 mb-2">
            {comic.title}
          </h1>
          <p className="text-sm md:text-base text-background-50/60">
            {comic.latestChapter} · 下拉继续阅读
          </p>
        </div>
      </div>
    </section>
  );
}

function ComicInfo({ comic }: { comic: ComicDetail }) {
  return (
    <section className="bg-background-50 py-10 md:py-14">
      <div className="w-full px-4 md:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
            <div className="lg:w-2/5">
              <span className="inline-block px-3 py-1 rounded-lg bg-secondary-100 text-secondary-700 text-xs font-medium mb-4">
                作品信息
              </span>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-1">
                {comic.title}
              </h2>
              <p className="text-base md:text-lg text-foreground-600 font-heading mb-6">
                {comic.author}
              </p>
              <p className="text-sm text-foreground-700 leading-relaxed mb-6">
                {comic.description}
              </p>
              <div className="flex items-center gap-3">
                <Link
                  to="#chapters"
                  className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-all whitespace-nowrap"
                >
                  开始阅读
                </Link>
                <button type="button" className="px-5 py-2 rounded-full bg-secondary-100 text-secondary-800 text-sm font-medium hover:bg-secondary-200 transition-all whitespace-nowrap cursor-pointer">
                  <i className="ri-bookmark-line mr-1"></i>加入书架
                </button>
              </div>
            </div>

            <div className="lg:w-3/5">
              <div className="flex flex-wrap gap-4 md:gap-6 mb-6">
                <div className="flex-1 min-w-[120px] bg-background-100 rounded-lg p-4 text-center">
                  <p className="text-xl md:text-2xl font-heading font-bold text-foreground-950">{comic.views}</p>
                  <p className="text-xs text-foreground-500 mt-1">总阅读</p>
                </div>
                <div className="flex-1 min-w-[120px] bg-background-100 rounded-lg p-4 text-center">
                  <p className="text-xl md:text-2xl font-heading font-bold text-foreground-950">{comic.rating}分</p>
                  <p className="text-xs text-foreground-500 mt-1">用户评分</p>
                </div>
                <div className="flex-1 min-w-[120px] bg-background-100 rounded-lg p-4 text-center">
                  <p className="text-xl md:text-2xl font-heading font-bold text-foreground-950">{comic.favorites}</p>
                  <p className="text-xs text-foreground-500 mt-1">收藏数</p>
                </div>
                <div className="flex-1 min-w-[120px] bg-background-100 rounded-lg p-4 text-center">
                  <p className="text-sm md:text-base font-heading font-bold text-accent-600">{comic.status}</p>
                  <p className="text-xs text-foreground-500 mt-1">更新状态</p>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="w-28 h-20 rounded-lg overflow-hidden">
                  <img
                    src={comic.screenshotUrls[0]}
                    alt={`${comic.title}精彩画面`}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="w-36 h-48 rounded-lg overflow-hidden">
                  <img
                    src={comic.screenshotUrls[1] || comic.screenshotUrls[0]}
                    alt={`${comic.title}精彩分镜`}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ChapterList({ comic }: { comic: ComicDetail }) {
  return (
    <section id="chapters" className="bg-background-100 py-10 md:py-14">
      <div className="w-full px-4 md:px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
            章节列表
          </h2>
          <div className="space-y-2">
            {comic.chapters.map((ch) => (
              <div
                key={ch.id}
                className="flex items-center justify-between bg-background-50 rounded-lg px-4 py-3 hover:bg-background-100 transition-colors cursor-pointer"
              >
                <span className="text-sm font-medium text-foreground-800">
                  第{ch.id}话 · {ch.title}
                </span>
                <span className="text-xs text-foreground-400">{ch.updateTime}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function RelatedComics({ comic }: { comic: ComicDetail }) {
  return (
    <section className="bg-background-50 py-10 md:py-14">
      <div className="w-full px-4 md:px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
            同类推荐
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {comic.relatedComics.map((rel) => (
              <Link
                key={rel.id}
                to={`/comic/${rel.id}`}
                className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300"
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={rel.coverUrl}
                    alt={rel.title}
                    title={`韩漫在线观看 - ${rel.title}`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3 px-2 py-0.5 rounded-full bg-primary-500 text-background-50 text-xs font-medium">
                    {rel.rating}分
                  </div>
                </div>
                <div className="p-3">
                  <h3 className="text-sm font-heading font-bold text-foreground-950 group-hover:text-primary-600 transition-colors">
                    {rel.title}
                  </h3>
                  <p className="text-xs text-foreground-500">{rel.author}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ReviewList({ comic }: { comic: ComicDetail }) {
  return (
    <section className="bg-background-100 py-10 md:py-14">
      <div className="w-full px-4 md:px-6">
        <div className="max-w-6xl mx-auto">
          <span className="inline-block px-3 py-1 rounded-lg bg-accent-100 text-accent-700 text-xs font-medium mb-4">
            读者评论
          </span>
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
            最新评论
          </h2>
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-5">
            {comic.reviews.map((review) => (
              <div key={review.id} className="break-inside-avoid mb-5 bg-background-50 rounded-lg p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                    <img
                      src={review.avatarUrl}
                      alt={review.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-950">{review.name}</p>
                    <p className="text-xs text-foreground-400">@{review.username} · {review.time}</p>
                  </div>
                </div>
                <div className="flex gap-0.5 mb-2">
                  {Array.from({ length: review.rating }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-accent-500 text-xs"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  「{review.content}」
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function ComicPage() {
  const { id } = useParams<{ id: string }>();
  const comic = getComicDetail(Number(id));

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-2">漫画未找到</h1>
            <Link to="/" className="text-primary-500 hover:underline">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Book',
    name: comic.title,
    author: { '@type': 'Person', name: comic.author },
    description: comic.description,
    genre: comic.category,
    url: `http://adeled.com/comic/${comic.id}`,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: String(comic.rating),
      bestRating: '10',
      ratingCount: parseInt(comic.views.replace('万', '0000').replace('.', ''), 10) || 1000,
    },
  };

  const jsonLdBreadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'http://adeled.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: comic.category,
        item: `http://adeled.com/category/${comic.categorySlug}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: comic.title,
        item: `http://adeled.com/comic/${comic.id}`,
      },
    ],
  };

  return (
    <>
      <Navbar />
      <main>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(jsonLdBreadcrumb)}</script>

        <ComicHero comic={comic} />

        <nav className="bg-background-50 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6 py-3">
            <div className="max-w-6xl mx-auto flex items-center gap-2 text-xs text-foreground-500">
              <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to={`/category/${comic.categorySlug}`} className="hover:text-primary-500 transition-colors">{comic.category}</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-800 font-medium">{comic.title}</span>
            </div>
          </div>
        </nav>

        <ComicInfo comic={comic} />
        <ChapterList comic={comic} />
        <RelatedComics comic={comic} />
        <ReviewList comic={comic} />
      </main>
      <Footer />
    </>
  );
}